import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { LanguageSelector } from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { localizedLandingCopy, localizedCapabilities } from './landingTranslations';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Check,
  CircleCheck,
  Clock3,
  Code2,
  Copy,
  Link2,
  Menu,
  Moon,
  MoreHorizontal,
  Plus,
  QrCode,
  Receipt,
  ShieldCheck,
  Sun,
  X,
} from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const copy = {
  it: {
    navLabel: 'Navigazione principale',
    menuOpen: 'Apri il menu di navigazione',
    menuClose: 'Chiudi il menu di navigazione',
    themeLight: 'Attiva il tema chiaro',
    themeDark: 'Attiva il tema scuro',
    eyebrow: 'Pagamenti fatti per il tuo lavoro',
    title: 'Al pagamento ci pensa Hola Pay. 🐱 Miao!',
    titleAccent: 'Hola Pay. 🐱 Miao!',
    intro: 'Link di pagamento, fatture e API in un unico spazio operativo. Hola Pay ti aiuta a incassare, controllare e riconciliare senza passaggi inutili.',
    heroImageAlt: 'Illustrazione Hola Pay con mascotte, link di pagamento e carta.',
    heroServicesLabel: 'TUTTO PER GESTIRE I TUOI INCASSI',
    heroServices: ['Link di pagamento', 'Fatture', 'API', 'Movimenti'],
    start: 'Crea il tuo account',
    login: 'Accedi',
    hasAccount: 'Hai già un account?',
    noteOne: 'Attivazione guidata',
    noteTwo: 'Incassi in EUR',
    noteThree: 'Supporto umano',
    workspace: 'Uno spazio per ogni incasso',
    workspaceIntro: 'Dalla prima richiesta al pagamento completato, ogni strumento resta collegato e leggibile.',
    activitySectionHeadline: 'Ogni movimento, sempre sotto controllo.',
    securitySectionHeadline: 'Protezione chiara per ogni pagamento.',
    capabilities: 'Tre modi pratici per incassare',
    activity: 'Attività recente',
    activityIntro: 'Segui link, fatture e pagamenti in tempo reale dal tuo spazio di lavoro.',
    details: 'Scopri come funziona',
    securityTitle: 'Più chiarezza, meno lavoro manuale.',
    securityBody: 'Permessi, eventi e stato dei pagamenti sono visibili in ogni momento. Il tuo team sa cosa è successo e cosa fare dopo.',
    finalTitle: 'Pronto per il prossimo pagamento?',
    finalBody: 'Inizia con un link e aggiungi il resto quando serve.',
    createLink: 'Crea un link',
    viewActivity: 'Vedi l’attività',
    footerLine: 'Pagamenti semplici per attività che stanno crescendo.',
    footerLanguages: 'Italiano · Inglese · Francese · Spagnolo · Arabo',
  },
  en: {
    navLabel: 'Main navigation',
    menuOpen: 'Open navigation menu',
    menuClose: 'Close navigation menu',
    themeLight: 'Use light theme',
    themeDark: 'Use dark theme',
    eyebrow: 'Payments made for your work',
    title: 'The straightforward way to get paid online.',
    titleAccent: 'online.',
    intro: 'Payment links, invoices, and APIs in one operational space. Hola Pay helps you collect, review, and reconcile without the extra steps.',
    heroImageAlt: 'Hola Pay illustration with its mascot, a payment link, and a card.',
    heroServicesLabel: 'TOOLS FOR YOUR PAYMENTS',
    heroServices: ['Payment links', 'Invoices', 'APIs', 'Activity'],
    start: 'Create your account',
    login: 'Sign in',
    hasAccount: 'Already have an account?',
    noteOne: 'Guided activation',
    noteTwo: 'EUR collections',
    noteThree: 'Human support',
    workspace: 'One workspace for every payment',
    workspaceIntro: 'From the first request to a completed payment, every tool stays connected and easy to read.',
    activitySectionHeadline: 'Every payment, in clear view.',
    securitySectionHeadline: 'Clear protection for every payment.',
    capabilities: 'Three practical ways to collect',
    activity: 'Recent activity',
    activityIntro: 'Follow links, invoices, and payments as they happen from your workspace.',
    details: 'See how it works',
    securityTitle: 'More clarity, less manual work.',
    securityBody: 'Permissions, events, and payment status stay visible at every step. Your team knows what happened and what to do next.',
    finalTitle: 'Ready for the next payment?',
    finalBody: 'Start with a link, then add the rest when you need it.',
    createLink: 'Create a link',
    viewActivity: 'View activity',
    footerLine: 'Simple payments for businesses on the move.',
    footerLanguages: 'Italian · English · French · Spanish · Arabic',
  },
};

const capabilities = {
  it: [
    {
      key: 'links',
      icon: Link2,
      label: 'Link di pagamento',
      title: 'Un link, una vendita, zero attrito.',
      description: 'Crea una pagina di pagamento condivisibile, aggiungi un importo e inviala dove i tuoi clienti sono già.',
      cta: 'Crea un link',
      route: '/register',
      points: ['Importo fisso o modificabile', 'QR pronto da mostrare', 'Stato del pagamento sempre visibile'],
    },
    {
      key: 'invoices',
      icon: Receipt,
      label: 'Fatture',
      title: 'Fatture che portano al pagamento.',
      description: 'Genera una fattura chiara, collegala al pagamento e tieni insieme documento, scadenza e incasso.',
      cta: 'Scopri le fatture',
      route: '/register',
      points: ['Numerazione ordinata', 'Scadenze sotto controllo', 'Invio con link incluso'],
    },
    {
      key: 'api',
      icon: Code2,
      label: 'API e integrazioni',
      title: 'Collega il tuo prodotto al denaro.',
      description: 'Un’API semplice e notifiche webhook affidabili per integrare i pagamenti nel tuo prodotto.',
      points: ['Chiavi separate per ambiente', 'Eventi webhook tracciabili', 'Ambiente di prova sicuro'],
    },
  ],
  en: [
    {
      key: 'links',
      icon: Link2,
      label: 'Payment links',
      title: 'One link, one sale, no friction.',
      description: 'Create a shareable checkout, add an amount, and send it where your customers already are.',
      cta: 'Create a link',
      route: '/register',
      points: ['Fixed or customer-set amount', 'QR ready to show', 'Payment status always visible'],
    },
    {
      key: 'invoices',
      icon: Receipt,
      label: 'Invoices',
      title: 'Invoices that lead to payment.',
      description: 'Make a clear invoice, connect it to checkout, and keep the document, due date, and payment together.',
      cta: 'Explore invoices',
      route: '/register',
      points: ['Consistent numbering', 'Due dates in view', 'Send with a payment link'],
    },
    {
      key: 'api',
      icon: Code2,
      label: 'API and integrations',
      title: 'Connect your product to money.',
      description: 'A readable API and dependable webhooks for payment flows inside your product.',
      points: ['Separate environment keys', 'Traceable webhook events', 'A sandbox for calm testing'],
    },
  ],
};

function ProductSidebar({ activeMode, setActiveMode, isDark, labels }) {
  return (
    <aside className="hola-product-sidebar">
      <div className="hola-product-sidebar-top">
        <div className="hola-product-logo"><span>H</span></div>
        <button type="button" className="hola-icon-button" aria-label={labels.menu} data-testid="button-demo-menu">
          <Menu size={16} />
        </button>
      </div>
      <div className="hola-sidebar-label">{labels.workspace}</div>
      <nav className="hola-product-nav" aria-label={labels.workspace}>
        {[
          { key: 'overview', label: labels.overview, icon: BarChart3 },
          { key: 'links', label: labels.links, icon: Link2 },
          { key: 'invoices', label: labels.invoices, icon: Receipt },
          { key: 'api', label: labels.api, icon: Code2 },
        ].map(({ key, label, icon: Icon }) => (
          <button
            type="button"
            key={key}
            className={`hola-product-nav-item ${activeMode === key ? 'is-active' : ''}`}
            onClick={() => setActiveMode(key)}
            data-testid={`button-demo-${key}`}
          >
            <Icon size={15} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="hola-sidebar-bottom">
        <div className="hola-sidebar-help"><ShieldCheck size={15} /> {labels.protected}</div>
        <div className={`hola-demo-user ${isDark ? 'is-dark' : ''}`}><span>MR</span><div><strong>Marina Rossi</strong><small>Workspace owner</small></div><MoreHorizontal size={14} /></div>
      </div>
    </aside>
  );
}

function StatusPill({ children, tone = 'green' }) {
  return <span className={`hola-status-pill ${tone}`}><span className="hola-status-dot" />{children}</span>;
}

function DashboardPreview({ isDark, language, activeMode, setActiveMode }) {
  const labels = language === 'it'
    ? { menu: 'Apri menu demo', workspace: 'Workspace', overview: 'Panoramica', links: 'Link di pagamento', invoices: 'Fatture', api: 'API e integrazioni', protected: 'Dati protetti' }
    : { menu: 'Open demo menu', workspace: 'Workspace', overview: 'Overview', links: 'Payment links', invoices: 'Invoices', api: 'API and integrations', protected: 'Protected data' };

  return (
    <div className={`hola-dashboard-shell ${isDark ? 'is-dark' : ''}`}>
      <ProductSidebar activeMode={activeMode} setActiveMode={setActiveMode} isDark={isDark} labels={labels} />
      <div className="hola-dashboard-main">
        <div className="hola-dashboard-toolbar">
          <div><span className="hola-overline">{language === 'it' ? 'Workspace' : 'Workspace'}</span><h3>{activeMode === 'api' ? 'API activity' : activeMode === 'invoices' ? 'Invoices' : activeMode === 'links' ? 'Payment links' : 'Overview'}</h3></div>
          <div className="hola-toolbar-actions"><span className="hola-live"><span />Live</span><button type="button" className="hola-icon-button" aria-label="More options" data-testid="button-demo-options"><MoreHorizontal size={17} /></button></div>
        </div>
        {activeMode === 'api' ? (
          <div className="hola-api-preview">
            <div className="hola-api-preview-head"><div><span className="hola-overline">Requests / today</span><strong>Connection healthy</strong></div><StatusPill>Operational</StatusPill></div>
            <div className="hola-code-window">
              <div className="hola-code-window-bar"><span /><span /><span /><small>create-payment-link.js</small></div>
              <pre><code>{`const payment = await hola.payments.create({\n  amount: 14800,\n  currency: 'EUR',\n  reference: 'order_1042'\n});`}</code></pre>
            </div>
            <div className="hola-api-events"><span><CircleCheck size={14} /> payment.created</span><span><CircleCheck size={14} /> webhook.delivered</span></div>
          </div>
        ) : activeMode === 'links' ? (
          <div className="hola-demo-link-layout">
            <div className="hola-demo-card hola-payment-link-card">
              <div className="hola-demo-card-head"><span className="hola-small-label">Payment link</span><StatusPill>Active</StatusPill></div>
              <div className="hola-link-brand"><div className="hola-mini-mark">H</div><div><strong>Studio North</strong><small>Creative direction</small></div></div>
              <div className="hola-demo-amount"><small>Amount due</small><strong>€ 148,00</strong></div>
              <div className="hola-payment-url"><Link2 size={14} /><span>pay.hola.it/studio-north</span><button type="button" aria-label="Copy payment link" data-testid="button-copy-payment-link"><Copy size={13} /></button></div>
            </div>
            <div className="hola-qr-card"><QrCode size={48} strokeWidth={1.4} /><strong>Show QR</strong><small>For in-person checkout</small></div>
          </div>
        ) : activeMode === 'invoices' ? (
          <div className="hola-invoice-preview">
            <div className="hola-invoice-paper">
              <div className="hola-invoice-head"><div className="hola-invoice-wordmark">HOLA <b>PAY</b></div><span>PAID</span></div>
              <div className="hola-invoice-meta"><div><small>Invoice</small><strong>HP-1042</strong></div><div><small>Due date</small><strong>18 Sep 2024</strong></div></div>
              <div className="hola-invoice-line"><span>Brand strategy sprint</span><strong>€ 860,00</strong></div>
              <div className="hola-invoice-total"><small>Total</small><strong>€ 860,00</strong></div>
            </div>
            <div className="hola-invoice-side"><StatusPill>Paid</StatusPill><small>Payment received</small><strong>Today, 09:42</strong><button type="button" className="hola-text-button" data-testid="button-demo-invoice">Open invoice <ArrowRight size={13} /></button></div>
          </div>
        ) : (
          <>
            <div className="hola-dashboard-summary">
              <div><span className="hola-small-label">Payment activity</span><strong>Everything in one view</strong></div>
              <button type="button" className="hola-yellow-button small" onClick={() => setActiveMode('links')} data-testid="button-demo-create-link"><Plus size={14} /> Create link</button>
            </div>
            <div className="hola-activity-list">
              {[
                { icon: Link2, title: 'Studio North', sub: 'Payment link · just now', amount: '€ 148,00', status: 'Paid', tone: 'green' },
                { icon: Receipt, title: 'Invoice HP-1042', sub: 'Northstar Studio · today', amount: '€ 860,00', status: 'Paid', tone: 'green' },
                { icon: Clock3, title: 'Web redesign deposit', sub: 'Payment link · awaiting payment', amount: '€ 1.200,00', status: 'Pending', tone: 'amber' },
              ].map(({ icon: Icon, title, sub, amount, status, tone }) => (
                <div className="hola-activity-row" key={title}><div className="hola-activity-icon"><Icon size={15} /></div><div className="hola-activity-copy"><strong>{title}</strong><small>{sub}</small></div><div className="hola-activity-amount"><strong>{amount}</strong><StatusPill tone={tone}>{status}</StatusPill></div></div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { currentLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const language = currentLanguage;
  const words = { ...(copy[language] || copy.en), ...(localizedLandingCopy[language] || {}) };
  const localizedItems = localizedCapabilities[language];
  const items = localizedItems
    ? localizedItems.map((item, index) => ({ ...capabilities.en[index], ...item }))
    : (capabilities[language] || capabilities.en);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [activeMode, setActiveMode] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      const token = localStorage.getItem('alphabit_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'super_admin') return;
      }
    } catch {}
    axios.post(`${API}/api/analytics/track?page=/&site=main`).catch(() => {});
  }, []);

  const navItems = words.navItems || (language === 'it'
    ? [{ label: 'Prodotto', href: '#prodotto' }, { label: 'Come funziona', href: '#come-funziona' }, { label: 'Sicurezza', href: '#sicurezza' }]
    : [{ label: 'Product', href: '#product' }, { label: 'How it works', href: '#how-it-works' }, { label: 'Security', href: '#security' }]);

  return (
    <div className={`hola-page ${isDark ? 'hola-page-dark' : ''}`} dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language}>
        <nav className="hola-nav" aria-label={words.navLabel}>
        <div className="hola-nav-inner">
          <Link to="/" className="hola-brand-link" data-testid="nav-logo"><img className="hola-brand-image" src="/holapay-mascot-logo.png" alt="Hola Pay" /></Link>
          <div id="hola-primary-navigation" className={`hola-nav-links ${mobileOpen ? 'is-open' : ''}`}>
            {navItems.map((item) => <a href={item.href} key={item.href} onClick={() => setMobileOpen(false)}>{item.label}</a>)}
            <Link to="/docs" onClick={() => setMobileOpen(false)}>{words.docs || (language === 'it' ? 'Documentazione' : 'Documentation')}</Link>
            <Link to="/login" className="hola-mobile-nav-login" onClick={() => setMobileOpen(false)}>{words.login}</Link>
          </div>
          <div className="hola-nav-actions">
            <LanguageSelector className="hola-language" />
            <button type="button" className="hola-theme-toggle" onClick={toggleTheme} aria-label={isDark ? words.themeLight : words.themeDark} data-testid="theme-toggle">{isDark ? <Sun size={16} /> : <Moon size={16} />}</button>
            <Link to="/login" className="hola-login-link">{words.login}</Link>
            <Link to="/register" className="hola-nav-cta" data-testid="nav-register">{words.start}</Link>
            <button type="button" className="hola-mobile-toggle" onClick={() => setMobileOpen((open) => !open)} aria-label={mobileOpen ? words.menuClose : words.menuOpen} aria-expanded={mobileOpen} aria-controls="hola-primary-navigation" data-testid="button-mobile-menu">{mobileOpen ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
        </div>
      </nav>

      <main>
        <section className="hola-hero">
          <div className="hola-hero-direct hola-reveal">
            <div className="hola-hero-heading">
              <span className="hola-eyebrow"><span className="hola-eyebrow-mark" aria-hidden="true" />{words.eyebrow}</span>
              <h1 id="hola-hero-title">
                {words.title.slice(0, -words.titleAccent.length)}
                <span>{words.titleAccent}</span>
              </h1>
              <p>{words.intro}</p>
            </div>
            <img
              className="hola-hero-direct-image"
              src="/hola-pay-hero-artwork.png"
              alt={words.heroImageAlt}
              width="1672"
              height="941"
              loading="eager"
              fetchPriority="high"
            />
            <div className="hola-hero-actions">
              <Link to="/register" className="hola-yellow-button" data-testid="hero-cta">{words.start}<ArrowRight size={17} /></Link>
            </div>
            <p className="hola-hero-account-prompt">
              {words.hasAccount}{' '}
              <Link to="/login">{words.login}</Link>
            </p>
            <div className="hola-trust-line">
              {[words.noteOne, words.noteTwo, words.noteThree].map((note) => <span key={note}><Check size={14} />{note}</span>)}
            </div>
          </div>
          <div className="hola-hero-tape">
            <span id="hola-hero-services-title" className="hola-hero-tape-label">{words.heroServicesLabel}</span>
            <ul className="hola-hero-tape-items" aria-labelledby="hola-hero-services-title">
              {words.heroServices.map((service) => (
                <li className="hola-hero-tape-item" key={service}>
                  <span className="hola-hero-tape-dot" aria-hidden="true" />
                  {service}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="hola-section hola-workspace" id={words.sectionIds?.product || (language === 'it' ? 'prodotto' : 'product')}>
          <div className="hola-container hola-workspace-content">
            <div className="hola-section-heading hola-workspace-heading">
              <span className="hola-overline">{words.workspaceOverline || `01 / ${language === 'it' ? 'Un unico flusso' : 'One connected flow'}`}</span>
              <h2>{words.workspace}</h2>
            </div>
            <div className="hola-tools-section-direct">
              <img
                className="hola-tools-section-image"
                src="/hola-pay-tools-section.png"
                alt={words.workspaceImageAlt || (language === 'it' ? 'Hola Pay: tre modi pratici per incassare e ogni pagamento ha un punto di partenza chiaro' : 'Hola Pay: three practical ways to collect payments and a clear starting point for every payment')}
              />
            </div>
          </div>
        </section>

         <section className="hola-product-story" id="hola-product-story" aria-labelledby="hola-product-story-title">
           <div className="hola-container hola-product-story-inner">
             <div className="hola-product-story-heading">
               <span className="hola-overline">{words.productStoryOverline || (language === 'it' ? 'MASCOTTE HOLA PAY / NEL PRODOTTO' : 'HOLA PAY CAT / IN THE PRODUCT')}</span>
               <h2 id="hola-product-story-title">{words.productStoryTitle || (language === 'it' ? 'Ti accompagno io, dal primo clic.' : 'I’ve got you, from the first click.')}</h2>
               <p>{words.productStoryBody || (language === 'it' ? 'Link, fatture e API lavorano insieme in una scena chiara, senza passaggi nascosti.' : 'Links, invoices, and APIs work together in one clear scene, with no hidden steps.')}</p>
             </div>
             <img
               className="hola-card-direct-image"
               src="/hola-pay-card-direct.png"
                alt={words.productStoryAlt || (language === 'it' ? 'Hola Pay Cat, link di pagamento, fatture automatiche e API integrate' : 'Hola Pay Cat with payment links, automatic invoices, and integrated APIs')}
             />
           </div>
         </section>

          <section
            className="hola-section hola-workbench-section"
            id={words.sectionIds?.howItWorks || (language === 'it' ? 'come-funziona' : 'how-it-works')}
            aria-labelledby="hola-workbench-title"
          >
           <div className="hola-container">
             <div className="hola-section-heading">
               <span className="hola-overline">{words.workbenchOverline || `03 / ${language === 'it' ? 'Nel lavoro quotidiano' : 'In the daily work'}`}</span>
                <h2 id="hola-workbench-title">
                   {words.workbenchTitleFirst
                     ? <>{words.workbenchTitleFirst}<br /><em>{words.workbenchTitleSecond}</em></>
                     : language === 'it'
                       ? <>Costruisci il flusso.<br /><em>Guardalo funzionare.</em></>
                       : <>Build the flow.<br /><em>Watch it work.</em></>}
                </h2>
                <img
                  className="hola-workbench-heading-image"
                  src="/hola-pay-workbench-dashboard.png"
                  alt={words.workbenchImageAlt || (language === 'it'
                    ? 'Dashboard Hola Pay con la mascotte, i pagamenti, i link attivi e le fatture'
                    : 'Hola Pay dashboard showing the mascot, payments, active links, and invoices')}
                  width="1672"
                  height="941"
                  loading="lazy"
                  decoding="async"
                />
               <p>{words.activityIntro}</p>
             </div>
             <div className="hola-workbench-direct-content">
               <div className="hola-workbench-copy">
                 <span className="hola-yellow-label">{words.checkoutLabel || (language === 'it' ? '01 / INCASSI' : '01 / CHECKOUT')}</span>
                  <h3>{words.checkoutTitleFirst
                    ? <>{words.checkoutTitleFirst}<br />{words.checkoutTitleSecond}</>
                    : language === 'it' ? <>Crea una volta.<br />Condividi ovunque.</> : <>Create once.<br />Share anywhere.</>}</h3>
                  <p>{words.checkoutBody || (language === 'it' ? 'Un link chiaro, un QR pronto, un pagamento che puoi seguire. Funziona nei messaggi, nella posta elettronica o al bancone.' : 'A clear link, a ready QR, a payment you can follow. Works in chat, email, or at the counter.')}</p>
                 <Link to="/register" className="hola-text-button" data-testid="workbench-link-cta">{words.createLink}<ArrowRight size={14} /></Link>
               </div>
               <div className="hola-workbench-direct-visual">
                 <img
                   className="hola-workbench-direct-image"
                   src="/hola-pay-checkout-direct.png"
                    alt={words.checkoutImageAlt || (language === 'it' ? 'Procedura Hola Pay per creare un pagamento sicuro' : 'Hola Pay Checkout for creating a secure payment')}
                 />
               </div>
             </div>
           </div>
         </section>

          <section
            className={`hola-section hola-activity-section${language === 'it' ? ' has-artwork' : ''}`}
            id={words.sectionIds?.activity || (language === 'it' ? 'attivita' : 'activity')}
            aria-labelledby="hola-activity-title"
          >
            {language === 'it' ? (
              <>
                <div className="hola-container hola-image-section-heading">
                  <div className="hola-section-heading">
                    <span className="hola-overline">{words.activityOverline || (language === 'it' ? 'Pagamenti / In tempo reale' : 'Live / Payments')}</span>
                    <h2 id="hola-activity-title">{words.activitySectionHeadline}</h2>
                  </div>
                </div>
                <div className="hola-activity-showcase">
                  <picture>
                    <img
                      className="hola-activity-showcase-image"
                      src="/hola-pay-activity.png"
                      alt={words.activityImageAlt || 'Anteprima Hola Pay: attività recente, pagamenti, link, fatture e aggiornamenti del portafoglio digitale.'}
                      width="1536"
                      height="1024"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <Link
                    to="/login"
                    className="hola-activity-showcase-cta"
                    aria-label={words.viewActivity}
                    title={words.viewActivity}
                    data-testid="activity-cta"
                  >
                    <span className="hola-visually-hidden">{words.viewActivity}</span>
                  </Link>
                </div>
              </>
            ) : (
              <div className="hola-container hola-activity-grid">
                <div className="hola-activity-copy">
                  <span className="hola-overline">{words.activityBoardOverline || '04 / Visibility'}</span>
                  <h2 id="hola-activity-title">{words.activity}</h2>
                  <p>{words.activityIntro}</p>
                  <Link to="/login" className="hola-outline-button dark-outline" data-testid="activity-cta">
                    {words.viewActivity}<ArrowRight size={16} />
                  </Link>
                </div>
                <div className="hola-activity-board">
                  <div className="hola-board-head">
                    <div><span className="hola-overline">{words.activity || 'Payment activity'}</span><strong>{words.activityBoardDate || 'Tuesday, 17 September'}</strong></div>
                    <StatusPill>{words.activityBoardLive || 'Live'}</StatusPill>
                  </div>
                  <div className="hola-board-list">{[
                    ['payment.created', 'Studio North', '€ 148,00', '09:42'],
                    ['invoice.paid', 'Northstar Studio', '€ 860,00', '09:18'],
                    ['link.opened', 'Web redesign deposit', words.activityBoardAwaitingPayment || 'Awaiting payment', '08:56'],
                    ['payout.scheduled', 'EUR balance', words.activityBoardTomorrow || 'Tomorrow', '08:40'],
                  ].map(([event, name, value, time], index) => (
                    <div className="hola-board-row" key={event}>
                      <span className={`hola-board-event ${index === 2 ? 'event-amber' : ''}`}><Activity size={13} /></span>
                      <div><strong>{name}</strong><small>{event}</small></div>
                      <span className="hola-board-value">{value}</span>
                      <time>{time}</time>
                    </div>
                  ))}</div>
                  <div className="hola-board-footer">
                    <span><span className="hola-live-dot" /> {words.activityBoardUpdates || 'Updates automatically'}</span>
                    <Link to="/login" data-testid="activity-board-link">{words.activityBoardOpenWorkspace || 'Open workspace'} <ArrowRight size={13} /></Link>
                  </div>
                </div>
              </div>
            )}
          </section>

        <section className="hola-security-section" id={words.sectionIds?.security || (language === 'it' ? 'sicurezza' : 'security')} aria-labelledby="hola-security-title">
          <div className="hola-container hola-security-direct">
            <div className="hola-section-heading hola-security-heading">
              <span className="hola-overline">{words.securityOverline || `Hola Pay / ${language === 'it' ? 'Protezione' : 'Security'}`}</span>
              <h2 id="hola-security-title">{words.securitySectionHeadline}</h2>
            </div>
            <img
              className="hola-security-direct-image"
              src="/hola-pay-security-direct.png"
              alt={words.securityImageAlt || (language === 'it' ? 'Hola Pay: più chiarezza e meno lavoro manuale' : 'Hola Pay: more clarity and less manual work')}
            />
          </div>
        </section>

        <section className="hola-final-cta"><div className="hola-container hola-final-inner"><div><span className="hola-eyebrow"><span className="hola-eyebrow-mark" />Hola Pay</span><h2>{words.finalTitle}</h2><p>{words.finalBody}</p></div><Link to="/register" className="hola-charcoal-button" data-testid="final-cta">{words.start}<ArrowRight size={17} /></Link></div></section>
      </main>

      <footer className="hola-footer"><div className="hola-container hola-footer-inner"><div><img className="hola-brand-image hola-brand-image-footer" src="/holapay-mascot-logo.png" alt="Hola Pay" /><p>{words.footerLine}</p><div className="hola-footer-contact"><a href="https://www.holapay.it" target="_blank" rel="noopener noreferrer">www.holapay.it</a><a href="mailto:info@holapay.it">info@holapay.it</a></div></div><div className="hola-footer-links"><Link to="/login">{words.login}</Link><Link to="/register">{words.start}</Link></div><div className="hola-footer-meta"><span>{words.footerLanguages}</span><span className="hola-footer-note">© Hola Pay</span></div></div></footer>

      <Dialog open={!!selectedFeature} onOpenChange={(open) => !open && setSelectedFeature(null)}>
        <DialogContent className={`hola-feature-dialog ${isDark ? 'is-dark' : ''}`}>
          {selectedFeature && <><DialogHeader><span className="hola-yellow-label">HOLA PAY / {selectedFeature.label}</span><DialogTitle>{selectedFeature.title}</DialogTitle><DialogDescription>{selectedFeature.description}</DialogDescription></DialogHeader><div className="hola-dialog-points">{selectedFeature.points.map((point) => <div key={point}><Check size={15} /><span>{point}</span></div>)}</div><div className="hola-dialog-actions">{selectedFeature.cta && selectedFeature.route && <Button className="hola-yellow-button" onClick={() => { setSelectedFeature(null); navigate(selectedFeature.route); }}>{selectedFeature.cta}<ArrowRight size={15} /></Button>}<Button variant="outline" onClick={() => setSelectedFeature(null)}>{words.close || (language === 'it' ? 'Chiudi' : 'Close')}</Button></div></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}